/**
 * Interactive world map of every festival. Uses Leaflet via react-leaflet
 * with OpenStreetMap tiles. Pins are colored by region; tapping one opens
 * a compact popup with an "Open festival" action.
 *
 * This module is intentionally isolated from the rest of the app so it can
 * be code-split later if bundle size becomes a concern.
 */
import React, { useEffect, useMemo, useRef } from "react";
import type { EventDTO } from "../lib/api";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Leaflet's default marker icon paths are bundler-hostile; we swap them for
// a small inline SVG that we can also tint per region.
const REGION_COLORS: Record<string, string> = {
  "North America": "#ff6a13",
  "Europe": "#30d7ff",
  "South America": "#51e078",
  "Asia": "#ff1f4a",
  "Oceania": "#c34dff",
  "Africa": "#ffdb4a",
};

function pinIcon(color: string): L.DivIcon {
  const html = `
    <svg viewBox="0 0 24 32" width="28" height="36" aria-hidden="true">
      <path d="M12 0 C5.4 0 0 5.4 0 12 c0 8 12 20 12 20 s12-12 12-20 C24 5.4 18.6 0 12 0 z"
            fill="${color}" stroke="#0a0b0e" stroke-width="1.2"/>
      <circle cx="12" cy="12" r="4.2" fill="#0a0b0e" stroke="${color}" stroke-width="1.2"/>
    </svg>`;
  return L.divIcon({
    html,
    className: "festival-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 34],
    popupAnchor: [0, -30],
  });
}

/** Auto-fit the map to every visible event on mount and when events change. */
function FitBoundsTo({ events }: { events: EventDTO[] }) {
  const map = useMap();
  useEffect(() => {
    const withCoords = events.filter(e => e.lat != null && e.lng != null);
    if (withCoords.length === 0) return;
    if (withCoords.length === 1) {
      const e = withCoords[0]!;
      map.setView([e.lat!, e.lng!], 6);
      return;
    }
    const bounds = L.latLngBounds(
      withCoords.map(e => [e.lat!, e.lng!] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
  }, [map, events]);
  return null;
}

export function FestivalMap({
  events,
  onPick,
}: {
  events: EventDTO[];
  onPick: (event: EventDTO) => void;
}) {
  const withCoords = useMemo(
    () => events.filter(e => e.lat != null && e.lng != null),
    [events],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  // When the parent width changes (e.g. portrait↔landscape), Leaflet needs
  // a hint to recompute tile layout. We do it on mount.
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (withCoords.length === 0) {
    return (
      <div className="empty" data-testid="map-empty">
        <div className="emoji">🗺️</div>
        No festivals to plot yet.
      </div>
    );
  }

  return (
    <div className="festival-map-wrap" ref={containerRef} data-testid="festival-map">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        worldCopyJump
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={18}
        />
        <FitBoundsTo events={withCoords} />
        {withCoords.map(ev => {
          const color = REGION_COLORS[ev.region ?? ""] ?? "#ff6a13";
          return (
            <Marker
              key={ev.slug}
              position={[ev.lat!, ev.lng!]}
              icon={pinIcon(color)}
            >
              <Popup closeButton={false}>
                <div className="festival-pin-popup">
                  <div className="festival-pin-name" style={{ color }}>
                    {ev.name}
                  </div>
                  <div className="festival-pin-meta">
                    {ev.location ?? ""}
                  </div>
                  <div className="festival-pin-meta">
                    {fmtDateRange(ev.startDate, ev.endDate)} · {ev.year}
                  </div>
                  <button
                    type="button"
                    className="festival-pin-open"
                    onClick={() => onPick(ev)}
                    data-testid={`pin-open-${ev.slug}`}
                  >
                    Open →
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function fmtDateRange(start: string, end: string): string {
  const [, m1, d1] = start.split("-");
  const [, m2, d2] = end.split("-");
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mo1 = MONTHS[parseInt(m1!, 10) - 1] ?? m1;
  const mo2 = MONTHS[parseInt(m2!, 10) - 1] ?? m2;
  if (mo1 === mo2) return `${mo1} ${parseInt(d1!, 10)}–${parseInt(d2!, 10)}`;
  return `${mo1} ${parseInt(d1!, 10)} – ${mo2} ${parseInt(d2!, 10)}`;
}
